import React, { useEffect, useMemo, useRef } from "react";

import CollectionCardRefactored, {
  type CollectionEntity,
} from "components/Collections/CollectionCardRefactored";
import type { GraphCollectionFeedEntry } from "lib/api/graphCollections";
import EmptyState from "ui/EmptyState";
import LoadingSkeleton from "ui/LoadingSkeleton";

interface GraphCollectionFeedProps {
  items: GraphCollectionFeedEntry[];
  isLoading: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  emptyTitle?: string;
  emptySubtitle?: string;
  loadingLabel?: string;
}

/**
 * Vertically paged feed of graph collection posts. Each entry renders as a
 * standard `CollectionCardRefactored` (EntityCard) so we inherit the full
 * entity toolbar — vote, save, share, comment, subscription, report, edit,
 * and default click-through to the `/collection/:id` profile. The graph
 * snapshot is supplied via the card's `graphSnapshot` prop so the interactive
 * canvas becomes the headline visual above the usual media carousel.
 */
const GraphCollectionFeed: React.FC<GraphCollectionFeedProps> = ({
  items,
  isLoading,
  hasNextPage = false,
  onLoadMore,
  emptyTitle,
  emptySubtitle,
  loadingLabel,
}) => {
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || !onLoadMore || !loadMoreSentinelRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      {
        rootMargin: "200px 0px",
      }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, onLoadMore]);

  const content = useMemo(() => {
    if (isLoading && items.length === 0) {
      return (
        <LoadingSkeleton
          variant="graph-card"
          count={2}
          ariaLabel={loadingLabel}
          containerClassName="mx-3"
        />
      );
    }

    if (!isLoading && items.length === 0) {
      return (
        <EmptyState
          title={emptyTitle ?? ""}
          subtitle={emptySubtitle ?? ""}
          className="m-4"
        />
      );
    }

    return items.map((item) => {
      const collection = item.post as unknown as CollectionEntity;
      return (
        <div key={String(item.post.id)} className="">
          <CollectionCardRefactored
            collection={collection}
            graphSnapshot={{
              nodes: item.nodes,
              edges: item.edges,
              rootNodeId: item.rootNodeId,
            }}
            truncateDescription={true}
            isEntityProfilePage={false}
          />
        </div>
      );
    });
  }, [isLoading, items, emptyTitle, emptySubtitle, loadingLabel]);

  return (
    <div className="pb-10">
      {content}
      {hasNextPage ? <div ref={loadMoreSentinelRef} className="h-8 w-full" /> : null}
    </div>
  );
};

export default GraphCollectionFeed;
